import React, { useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import InputAdornment from '@mui/material/InputAdornment';
import palette from '@ui/palette';
import Icon from '@ui/Icon';
import { MuiIconButton } from '@ui/base_library';
import TextField, { TextFieldProps } from '../TextField';
import createSyntheticEvent from '../../../util/createSyntheticEvent';

interface SearchFieldProps extends TextFieldProps {
  visible?: boolean;
  onClear?: () => void;
  disabled?: boolean;
}

const PADDING_Y = 1;
const PADDING_X_WITHOUT_ADORNMENT = 2.25;
const PADDING_X_WITH_ADORNMENT = 4;

const useStyles = makeStyles()(theme => ({
  field: {
    position: 'relative',
    '& > input': {
      '&.MuiInputBase-inputAdornedStart': {
        padding: theme.spacing(
          PADDING_Y,
          PADDING_X_WITH_ADORNMENT,
          PADDING_Y,
          PADDING_X_WITH_ADORNMENT
        ),
      },
      padding: theme.spacing(
        PADDING_Y,
        PADDING_X_WITHOUT_ADORNMENT,
        PADDING_Y,
        PADDING_X_WITHOUT_ADORNMENT
      ),
      // Note: this will trigger zooming on iphone due to font size being below 16px
      fontSize: theme.spacing(1.5),

      '&:focus:not(&:disabled)': {
        outline: `1px solid ${theme.palette.inputs.focus} !important`,
      },
    },
  },
  search: {
    position: 'absolute',
    left: theme.spacing(0.5),
  },
  searchClear: {
    position: 'absolute',
    right: theme.spacing(-0.1),
  },
  endIcon: {
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '&:focus': {
      // override the hover state in favor of a specific focus color
      color: `${theme.palette.inputs.focus}!important`,
    },
  },
  inputRoot: {
    borderRadius: 100,
    borderColor: `${palette.gray_300} !important`,
  },
  searchIcon: {
    color: palette.gray_75,
  },
  // note: placeholder color
  searchIconDisabled: {
    color: palette.gray_75,
  },
}));

const SearchField = ({
  onClear = () => {},
  autoFocus = false,
  visible = true,
  placeholder = 'Search',
  className,
  value,
  disabled = false,
  ...props
}: SearchFieldProps) => {
  const { classes } = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClear = () => {
    // eslint-disable-next-line no-unused-expressions
    inputRef.current?.focus();
    onClear();
  };

  const { onBlur, ref, ...propsWithoutBlur } = props;

  const triggerOnBlur = useCallback(
    (event: any) => {
      if (onBlur) {
        onBlur(event);
      }
    },
    [onBlur]
  );

  const handleBlur = useCallback(
    (e: any) => {
      const targetIsButton = e.relatedTarget === buttonRef.current;
      const targetIsInput = e.relatedTarget === inputRef.current;
      if (targetIsButton || targetIsInput) {
        e.preventDefault();
        return;
      }
      const notFromButton = e.target !== buttonRef.current;
      if (notFromButton) {
        triggerOnBlur(e);
        return;
      }
      // simulate button blur as input blur event
      const event = new Event('blur', { bubbles: true });
      Object.defineProperty(event, 'target', {
        writable: false,
        value: inputRef.current,
      });
      // casting due to having an incomplete target
      const syntheticEvent = createSyntheticEvent(event) as React.ChangeEvent<
        typeof inputRef.current
      >;
      triggerOnBlur(syntheticEvent);
    },
    [triggerOnBlur]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('blur', handleBlur, { capture: true });
      return () => {
        container.removeEventListener('blur', handleBlur, { capture: true });
      };
    }
  }, [containerRef, handleBlur]);
  /**
   * @description noting autofocus does not work when diplay is set to none initially
   * we are using `display: none` vs non-rendering due to this open issue on github
   * `React onBlur events not firing during unmount`
   *
   * @see https://github.com/facebook/react/issues/12363
   * @see https://github.com/facebook/react/issues/12363#issuecomment-1290762751
   */
  useEffect(() => {
    if (visible && autoFocus) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [visible, autoFocus]);
  return (
    <Box
      {...{ ref: containerRef }}
      style={{ display: visible ? 'block' : 'none', width: '100%' }}
    >
      <TextField
        {...propsWithoutBlur}
        placeholder={placeholder}
        value={value}
        className={clsx(classes.field, className)}
        inputRef={inputRef}
        disabled={disabled}
        role="search"
        inputProps={{
          className: clsx(classes.inputRoot),
        }}
        onKeyDown={e => {
          const keyIsEscape = e.key === 'Escape';
          if (keyIsEscape && value) {
            e.stopPropagation();
          }
          if (keyIsEscape) {
            handleClear();
          }
        }}
        startAdornment={
          !value && (
            <InputAdornment
              suppressHydrationWarning
              position="start"
              className={classes.search}
            >
              <MuiIconButton aria-label={'Search clips'} disabled={disabled}>
                <Icon
                  type="search"
                  className={clsx(
                    classes.searchIcon,
                    disabled && classes.searchIconDisabled
                  )}
                />
              </MuiIconButton>
            </InputAdornment>
          )
        }
        endAdornment={
          Boolean(value) && (
            <InputAdornment
              suppressHydrationWarning
              position="end"
              className={classes.searchClear}
            >
              <MuiIconButton
                onClick={handleClear}
                className={classes.endIcon}
                ref={buttonRef}
              >
                <Icon type="close" />
              </MuiIconButton>
            </InputAdornment>
          )
        }
      />
    </Box>
  );
};

export default SearchField;
